/**
 * Tiny fetch wrapper. Every request carries X-Requested-With (the backend's
 * CSRF backstop on mutations) and JSON-encodes bodies; non-2xx responses
 * throw an ApiError carrying the backend's { error } message.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Validation errors (422 from the config editor). */
    readonly errors: string[] = [],
  ) {
    super(message);
  }
}

export async function api<T = unknown>(path: string, opts: { method?: "GET" | "POST" | "PUT" | "DELETE"; body?: unknown } = {}): Promise<T> {
  const res = await fetch(path, {
    method: opts.method ?? "GET",
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let message = res.statusText;
    let errors: string[] = [];
    try {
      const body = (await res.json()) as { error?: string; errors?: string[] };
      if (Array.isArray(body.errors)) {
        errors = body.errors;
        message = `${errors.length} validation error${errors.length > 1 ? "s" : ""}`;
      } else message = body.error ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message, errors);
  }
  return res.json() as Promise<T>;
}

export const post = <T = unknown>(path: string, body?: unknown) => api<T>(path, { method: "POST", body });
export const del = <T = unknown>(path: string) => api<T>(path, { method: "DELETE" });
